import checkBrowser from './checkBrowser.js';
import { isPromise, suppressUnhandledRejectionWarning } from './helpers.js';
import {logError} from '../fingerprint.js';
import {noopLap} from './timings.js';


const SpecialFingerprint = Object.freeze({
    KnownForSuspending: -1,
    NotSupported: -2,
    Timeout: -3,
    KnownForAntifingerprinting: -4,
});

const InnerErrorName = Object.freeze({
    Timeout: 'timeout',
    Suspended: 'suspended',
});

function doesBrowserSuspendAudioContext(window) {
    const browser = checkBrowser(window);
    return browser.isWebKit && !browser.isDesktopWebKit && !browser.isWebKit606OrNewer;
}

function doesBrowserPerformAntifingerprinting(window) {
    const browser = checkBrowser(window);
    return browser.isWebKit && browser.isWebKit616OrNewer && browser.isSafariWebKit;
}

function makeInnerError(name) {
    const error = new Error(name);
    error.name = name;
    return error;
}

function startRenderingAudio(context) {
    const renderTryMaxCount = 3;
    const renderRetryDelay = 500;
    const runningMaxAwaitTime = 500;
    const runningSufficientTime = 5000;
    let finalize = () => undefined;

    const resultPromise = new Promise((resolve, reject) => {
        let isFinalized = false;
        let renderTryCount = 0;
        let startedRunningAt = 0;

        context.oncomplete = event => resolve(event.renderedBuffer);

        const startRunningTimeout = () => {
            setTimeout(
                () => reject(makeInnerError(InnerErrorName.Timeout)),
                Math.min(runningMaxAwaitTime, startedRunningAt + runningSufficientTime - Date.now()),
            );
        };

        const tryRender = () => {
            try {
                const renderingPromise = context.startRendering();

                if (isPromise(renderingPromise)) {
                    suppressUnhandledRejectionWarning(renderingPromise);
                }

                switch (context.state) {
                    case 'running':
                        startedRunningAt = Date.now();
                        if (isFinalized) {
                            startRunningTimeout();
                        }
                        break;

                    case 'suspended':
                        if (!document.hidden) {
                            renderTryCount++;
                        }
                        if (isFinalized && renderTryCount >= renderTryMaxCount) {
                            reject(makeInnerError(InnerErrorName.Suspended));
                        } else {
                            setTimeout(tryRender, renderRetryDelay);
                        }
                        break;
                    default:
                        break;
                }
            } catch (error) {
                reject(error);
            }
        };

        tryRender();

        finalize = () => {
            if (!isFinalized) {
                isFinalized = true;
                if (startedRunningAt > 0) {
                    startRunningTimeout();
                }
            }
        };
    });

    return [resultPromise, finalize];
}

function getHash(signal) {
    let hash = 0;
    for (const element of signal) {
        hash += Math.abs(element);
    }
    return hash;
}

function getUnstableAudioFingerprint(window) {
    const w = window;
    const AudioContext = w.OfflineAudioContext || w.webkitOfflineAudioContext;
    if (!AudioContext) {
        return SpecialFingerprint.NotSupported;
    }

    if (doesBrowserSuspendAudioContext(window)) {
        return SpecialFingerprint.KnownForSuspending;
    }

    const hashFromIndex = 4500;
    const hashToIndex = 5000;
    const context = new window.OfflineAudioContext(1, hashToIndex, 44100);

    const oscillator = context.createOscillator();
    oscillator.type = 'triangle';
    oscillator.frequency.value = 10000;

    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -50;
    compressor.knee.value = 40;
    compressor.ratio.value = 12;
    compressor.attack.value = 0;
    compressor.release.value = 0.25;

    oscillator.connect(compressor);
    compressor.connect(context.destination);
    oscillator.start(0);

    const [renderPromise, finishRendering] = startRenderingAudio(context);
    const fingerprintPromise = suppressUnhandledRejectionWarning(
        renderPromise.then(
            buffer => getHash(buffer.getChannelData(0).subarray(hashFromIndex)),
            error => {
                if (error.name === InnerErrorName.Timeout || error.name === InnerErrorName.Suspended) {
                    return SpecialFingerprint.Timeout;
                }
                throw error;
            },
        ),
    );

    return () => {
        finishRendering();
        return fingerprintPromise;
    };
}

function getAudioFingerprint(window) {
    if (doesBrowserPerformAntifingerprinting(window)) {
        return SpecialFingerprint.KnownForAntifingerprinting;
    }

    return getUnstableAudioFingerprint(window);
}

/**
 * @param {Window} window
 * @param {Promise<void>} timersReady Рендеринг стартует сразу, а таймаут ожидания результата (finalize)
 *     отсчитывается после этого сигнала, когда главный поток свободен (см. fingerprint.js).
 * @param {function(string)} lap Отметка этапа для замеров (timings.js).
 * @returns {Promise<{SOUNDCARD_HASH: (string|null)}>}
 */
async function getSoundcardHash(window, timersReady = Promise.resolve(), lap = noopLap) {
    try {
        if (typeof getAudioFingerprint(window) !== 'function') {
            // Быстрый выход: Safari 17+ с защитой от отпечатка, старый WebKit или нет OfflineAudioContext.
            lap('skip');
            return { SOUNDCARD_HASH: null };
        }

        const finishFingerprint = getAudioFingerprint(window);
        lap('setup');
        await timersReady;
        lap('wait');
        const hash = await finishFingerprint();
        lap('render');
        return { SOUNDCARD_HASH: hash?.toString() || null };
    } catch (e) {
        logError(e);
        return { SOUNDCARD_HASH: null };
    }
}

export default getSoundcardHash;
